import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { X, FileText, UploadCloud } from 'lucide-react'
import { useSaveUserDocument } from '@/api/hooks'
import { useConfirm } from '@/shared/ui/ConfirmProvider'
import { toast } from '@/store/useToastStore'

const DOC_TYPES = ['Driving Licence', 'Emirates ID', 'Passport', 'Visa', 'Other']
const DOC_TYPE_KEYS: Record<string, string> = {
  'Driving Licence': 'documentModal.docTypeDrivingLicence',
  'Emirates ID': 'documentModal.docTypeEmiratesId',
  'Passport': 'documentModal.docTypePassport',
  'Visa': 'documentModal.docTypeVisa',
  'Other': 'documentModal.docTypeOther',
}
const sides = (t: TFunction) => [
  { value: '', label: t('documentModal.sideSingle') },
  { value: 'FRONT', label: t('documentModal.sideFront') },
  { value: 'BACK', label: t('documentModal.sideBack') },
]

type Props = {
  open: boolean
  onClose: () => void
  userId: number | string
  doc?: any | null   // null/undefined → add mode
  onSaved: () => void
}

export function DocumentModal({ open, onClose, userId, doc, onSaved }: Props) {
  const { t } = useTranslation()
  const confirm = useConfirm()
  const isEdit = !!doc?.id
  const save = useSaveUserDocument()

  const [docType, setDocType] = useState('')
  const [customType, setCustomType] = useState('')
  const [side, setSide] = useState('')
  const [file, setFile] = useState<File | null>(null)

  useEffect(() => {
    if (!open) return
    const existingType = doc?.doc_type ?? doc?.document_type ?? ''
    const isPreset = DOC_TYPES.slice(0, -1).includes(existingType)
    setDocType(existingType && !isPreset ? 'Other' : existingType)
    setCustomType(existingType && !isPreset ? existingType : '')
    setSide(doc?.doc_side ?? '')
    setFile(null)
  }, [open, doc])

  if (!open) return null

  const resolvedType = docType === 'Other' ? customType.trim() : docType

  const save_ = async () => {
    if (!resolvedType) { toast.error(t('documentModal.selectDocType')); return }
    if (!isEdit && !file) { toast.error(t('documentModal.chooseFileToUpload')); return }
    if (isEdit && !(await confirm({
      title: t('common.confirmSaveTitle'), description: t('common.confirmSaveDescription'),
      confirmLabel: t('common.saveChanges'), variant: 'primary',
    }))) return

    const fd = new FormData()
    if (isEdit) fd.append('id', String(doc.id))
    fd.append('user_id', String(userId))
    fd.append('doc_type', resolvedType)
    if (side) fd.append('doc_side', side)
    if (file) fd.append('doc_file', file)

    save.mutate(fd, {
      onSuccess: () => {
        toast.success(isEdit ? t('documentModal.documentUpdated') : t('documentModal.documentAdded'))
        onSaved()
        onClose()
      },
      onError: (e: any) => {
        const errs = e?.response?.data?.errors
        toast.error(errs
          ? (typeof errs === 'string' ? errs : Object.values(errs).flat().join(', '))
          : (e?.response?.data?.message ?? t('documentModal.failedSaveDocument')))
      },
    })
  }

  const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.4)' }}>
      <div className="bg-white rounded-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="font-bold text-gray-900">{isEdit ? t('documentModal.editDocumentTitle') : t('documentModal.addNewDocumentTitle')}</h3>
          <button onClick={onClose}><X size={18} className="text-gray-400 hover:text-gray-600" /></button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">{t('documentModal.documentType')} *</label>
            <select value={docType} onChange={e => setDocType(e.target.value)} className={inputCls}>
              <option value="">{t('documentModal.selectType')}</option>
              {DOC_TYPES.map(dt => <option key={dt} value={dt}>{t(DOC_TYPE_KEYS[dt])}</option>)}
            </select>
          </div>

          {docType === 'Other' && (
            <div>
              <label className="text-xs font-semibold text-gray-500 block mb-1">{t('documentModal.customType')} *</label>
              <input value={customType} onChange={e => setCustomType(e.target.value)} placeholder={t('documentModal.customTypePlaceholder')} className={inputCls} />
            </div>
          )}

          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">{t('documentModal.side')}</label>
            <select value={side} onChange={e => setSide(e.target.value)} className={inputCls}>
              {sides(t).map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">
              {t('documentModal.file')} {isEdit && <span className="text-gray-400 font-normal">{t('documentModal.keepCurrentFile')}</span>}{!isEdit && '*'}
            </label>
            <label className="flex items-center gap-2 border border-dashed border-gray-300 rounded-lg px-3 py-3 text-sm text-gray-500 cursor-pointer hover:bg-gray-50">
              {file ? <FileText size={15} className="text-brand-500 flex-shrink-0" /> : <UploadCloud size={15} className="text-gray-400 flex-shrink-0" />}
              <span className="truncate">{file?.name ?? t('documentModal.chooseFile')}</span>
              <input type="file" accept="image/*,.pdf" className="hidden" onChange={e => setFile(e.target.files?.[0] ?? null)} />
            </label>
          </div>
        </div>

        <div className="flex gap-3 px-5 py-4 border-t border-gray-100">
          <button onClick={onClose} className="flex-1 py-2 border border-gray-200 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-50">{t('common.cancel')}</button>
          <button onClick={save_} disabled={save.isPending} className="flex-1 py-2 rounded-lg text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50" style={{ background: '#f68b2c' }}>
            {save.isPending ? t('documentModal.saving') : isEdit ? t('documentModal.updateDocument') : t('documentModal.addDocument')}
          </button>
        </div>
      </div>
    </div>
  )
}
